import React, { useState, useReducer, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
	GridComponent,
	ColumnsDirective,
	ColumnDirective,
	Inject,
	Page,
} from "@syncfusion/ej2-react-grids";

// Components
import Spinner from "../Components/Spinner";
import ToastError from "../Components/ToastError";
import InputField from "../Components/Forms/InputField";
import SelectField from "../Components/Forms/SelectField";

// Services
import { networkService } from "../../Services/network.service";
import { supplierService } from "../../Services/suppliers.service";

// Icons
import { AiFillSave } from "react-icons/ai";
import { FcSimCardChip } from "react-icons/fc";
import { FaEdit, FaTrash } from "react-icons/fa";

const _pageTitle = "Cadastro de Operadoras";
const _registerTitle = "Cadastrando nova Operadora";
const _editTitle = "Editando Operadora";

const formInitialValues = {
	id: 0,
	networkName: "",
	chipSupplierId: "",
};

const formReducer = (state, event) => {
	if (event.setter) {
		return event.network;
	}
	if (event.reset) {
		return formInitialValues;
	}

	return {
		...state,
		[event.name]: event.value,
	};
};

const filterReducer = (state, event) => {
	if (event.reset) {
		return { chipSupplierId: "" };
	}
	return {
		...state,
		[event.name]: event.value,
	};
};

const alertReducer = (state, event) => {
	if (event.reset) {
		return { show: false, message: "", color: "danger" };
	}
	return {
		...state,
		show: event.show,
		message: event.message,
		color: event.color,
	};
};

const mapNetworkToForm = (network) => ({
	id: network?.NetworkId ?? network?.networkId ?? network?.id ?? 0,
	networkName: network?.NetworkName ?? network?.networkName ?? "",
	chipSupplierId:
		network?.ChipSupplierId ?? network?.chipSupplierId ?? "",
});

const mapNetworkToGrid = (network, supplierOptions = []) => {
	const chipSupplierId = network?.ChipSupplierId ?? network?.chipSupplierId ?? "";
	const supplier = supplierOptions.find(
		(s) => String(s.id) === String(chipSupplierId)
	);
	
	return {
		id: network?.NetworkId ?? network?.networkId ?? network?.id ?? 0,
		networkName: network?.NetworkName ?? network?.networkName ?? "",
		chipSupplierId: chipSupplierId,
		supplierName: supplier?.name ?? chipSupplierId,
		active: network?.Active ?? network?.active ?? "",
	};
};

const mapSupplierToOption = (supplier) => ({
	key: supplier?.SupplierId ?? supplier?.supplierId ?? supplier?.id ?? crypto.randomUUID(),
	id: supplier?.SupplierId ?? supplier?.supplierId ?? supplier?.id ?? "",
	name:
		supplier?.SupplierCode ??
		supplier?.supplierCode ??
		supplier?.SupplierName ??
		supplier?.supplierName ??
		"",
});

export default function Networks() {
	const [formData, setFormData] = useReducer(formReducer, formInitialValues);
	const [isLoading, setIsLoading] = useState(false);
	const [showToast, setShowToast] = useState(false);
	const [actionTitle, setActionTitle] = useState("Dado da operadora");
	const [showFields, setShowFields] = useState(false);
	const [filter, setFilter] = useReducer(filterReducer, {
		chipSupplierId: "",
	});
	const [supplierOptions, setSupplierOptions] = useState([]);
	const [networkResults, setNetworkResults] = useState([]);
	const [alert, setAlert] = useReducer(alertReducer, {
		show: false,
		message: "",
		color: "danger",
	});
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [networkToDelete, setNetworkToDelete] = useState(null);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch({ type: "title", value: _pageTitle });
	}, [dispatch]);

	useEffect(() => {
		(async () => {
			try {
				const suppliers = await supplierService.list({ Active: "S" });
				if (suppliers && suppliers.length > 0) {
					setSupplierOptions(suppliers.map((item) => mapSupplierToOption(item)));
				}
			} catch (error) {
				setSupplierOptions([]);
			}
		})();
	}, []);

	const fetchNetworks = async (filters) => {
		setAlert({ reset: true });

		try {
			setIsLoading(true);
			const networks = await networkService.list({
				NetworkId: filters?.networkId ?? 0,
				ChipSupplierId: filters?.chipSupplierId,
				Active: "S",
			});

			if (networks && networks.length > 0) {
			setNetworkResults(networks.map((item) => mapNetworkToGrid(item, supplierOptions)));
			} else {
				setNetworkResults([]);
				setAlert({
					show: true,
					message: "Nenhuma operadora encontrada para o filtro informado.",
					color: "warning",
				});
			}
		} catch (error) {
			setAlert({
				show: true,
				message: "Não foi possível realizar a busca no momento.",
				color: "danger",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const onNew = () => {
		setAlert({ reset: true });
		setFormData({ reset: true });
		setShowFields(true);
		setActionTitle(_registerTitle);
	};

	const onCancel = () => {
		setAlert({ reset: true });
		setFormData({ reset: true });
		setShowFields(false);
	};

	const onCloseToast = () => {
		setShowToast(false);
	};

	const onSave = async () => {
		if (!formData.networkName) {
			setAlert({
				show: true,
				message: "Forneça o Nome da Operadora!",
				color: "danger",
			});
			return;
		}

		if (!formData.chipSupplierId) {
			setAlert({
				show: true,
				message: "Forneça o Fornecedor de Chip!",
				color: "danger",
			});
			return;
		}

		try {
			setIsLoading(true);
			const payload = {
				id: formData.id,
				networkName: formData.networkName,
				chipSupplierId: formData.chipSupplierId,
			};

			const createdOrUpdatedId = await networkService.save(payload);
			setFormData({
				name: "id",
				value: createdOrUpdatedId,
			});
			
			if (createdOrUpdatedId) {
				// Recarrega a grid com o filtro atual
				const networksAfterSave = await networkService.list({
					NetworkId: 0,
					ChipSupplierId: filter?.chipSupplierId,
					Active: "S",
				});

				if (networksAfterSave && networksAfterSave.length > 0) {
					setNetworkResults(networksAfterSave.map((item) => mapNetworkToGrid(item, supplierOptions)));
					setAlert({
						show: true,
						message: "Operadora salva com sucesso!",
						color: "success",
					});
				} else {
					setNetworkResults([]);
				}

				setShowFields(false);
			}
		} catch (error) {
			setAlert({
				show: true,
				message: "Não foi possível salvar. tente novamente mais tarde.",
				color: "danger",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const onEditNetwork = (row) => {
		setFormData({
			setter: true,
			network: mapNetworkToForm(row),
		});
		setShowFields(true);
		setActionTitle(_editTitle);
	};

	const onDeleteNetwork = (row) => {
		const networkId = row?.id;
		if (!networkId) {
			return;
		}

		setNetworkToDelete(row);
		setShowDeleteModal(true);
	};

	const confirmDeleteNetwork = async () => {
		const networkId = networkToDelete?.id;
		if (!networkId) {
			return;
		}

		try {
			setIsLoading(true);
			await networkService.remove(networkId);
			setNetworkResults((prev) => prev.filter((item) => item.id !== networkId));
			setShowDeleteModal(false);
			setNetworkToDelete(null);
			setAlert({
				show: true,
				message: "Operadora excluída com sucesso!",
				color: "success",
			});

			if (formData?.id === networkId) {
				setFormData({ reset: true });
				setShowFields(false);
			}
		} catch (error) {
			setAlert({
				show: true,
				message: "Não foi possível excluir a operadora.",
				color: "danger",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleFilterChange = (event) => {
		const nextFilter = {
			...filter,
			[event.target.name]: event.target.value.trim(),
		};

		setFilter({
			name: event.target.name,
			value: event.target.value.trim(),
		});

		if (event.target.name === "chipSupplierId") {
			if (!nextFilter.chipSupplierId) {
				setAlert({ reset: true });
				setNetworkResults([]);
				return;
			}

			setShowFields(false);
			fetchNetworks(nextFilter);
		}
	};

	const handleFormChange = async (event) => {
		setFormData({
			name: event.target.name,
			value: event.target.value,
		});
	};

	return (
		<>
			<div className="container-xxl flex-grow-1 container-p-y">
				{isLoading ? <Spinner /> : ""}

				<ToastError show={showToast} closeToast={onCloseToast} />

				<Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
					<Modal.Header closeButton>
						<Modal.Title>Confirmar exclusão</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						Deseja realmente excluir a operadora
						{" "}
						<strong>{networkToDelete?.id}</strong>
						?
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
							Cancelar
						</Button>
						<Button variant="danger" onClick={confirmDeleteNetwork}>
							Excluir
						</Button>
					</Modal.Footer>
				</Modal>

				<div className="row">
					<h5>Selecione o fornecedor para carregar as operadoras ou crie uma nova</h5>
					<div className="row">
						<div className="mb-3 col-md-4">
							<div className="row mb-3" style={{ whiteSpace: "nowrap" }}>
								<SelectField
									name="chipSupplierId"
									label="Fornecedor de Chip"
									tabIndex="1"
									grid_length="10"
									value={filter?.chipSupplierId || ""}
									itens={supplierOptions}
									onChange={handleFilterChange}
								/>
							</div>
						</div>

						<div className="mb-3 col-md-8 col-lg-8" style={{marginTop: 30}}>
							<div className="d-flex justify-content-end">
								<button
									type="button"
									className="btn btn-primary"
									disabled={showFields}
									onClick={onNew}
									tabIndex={2}
								>
									<FcSimCardChip className="icon-in-button" />
									&nbsp; Novo
								</button>
							</div>
						</div>
					</div>
				</div>

				{alert?.show ? (
					<div
						className={`alert alert-${alert.color} alert-dismissible`}
						role="alert"
					>
						<div dangerouslySetInnerHTML={{ __html: alert?.message }}></div>

						<button
							type="button"
							className="btn-close"
							data-bs-dismiss="alert"
							onClick={() => setAlert({ show: false, message: "" })}
							aria-label="Close"
						></button>
					</div>
				) : (
					""
				)}

				<div
					className="row"
					style={showFields ? { display: "" } : { display: "none" }}
				>
					<h4 className="fw-bold py-3 mb-4">
						<span className="text-muted fw-light">{actionTitle} </span>
					</h4>

					{formData.id ? (
						<div className="row mb-1">
							<label className="col-sm-4 col-form-label" htmlFor="networkName">
								Id: {formData.id || ""}
							</label>
						</div>
					) : (
						""
					)}

					<div className="row">
						<div className="col-md-6 mb-3">
							<InputField
								label="Nome da Operadora"
								name="networkName"
								type="text"
								tabIndex="5"
								grid_length="12"
								value={formData.networkName || ""}
								onChange={handleFormChange}
							/>
						</div>
						<div className="col-md-6 mb-3">
							<SelectField
								name="chipSupplierId"
								label="Fornecedor de Chip"
								tabIndex="6"
								grid_length="12"
								value={formData.chipSupplierId || ""}
								itens={supplierOptions}
								onChange={handleFormChange}
							/>
						</div>
					</div>

					<div className="mt-4">
						<button
							type="button"
							tabIndex={7}
							className="btn btn-primary me-2"
							onClick={async () => onSave()}
						>
							<AiFillSave className="icon-in-button" />
							&nbsp; Salvar
						</button>

						<button
							type="reset"
							className="btn btn-outline-secondary  me-2"
							onClick={onCancel}
							tabIndex={8}
						>
							Cancelar
						</button>
					</div>
				</div>
			</div>

				{!showFields && networkResults.length > 0 ? (
					<div className="container-xxl flex-grow-1 container-p-y pt-0">
						<div className="row mt-4">
							<h5>Resultado da busca</h5>
							<GridComponent
								dataSource={networkResults}
								allowPaging={true}
								pageSettings={{ pageSize: 10 }}
							>
								<ColumnsDirective>
									<ColumnDirective
										field="networkName"
										headerText="Nome de Operadora"
										width="90"
										textAlign="Left"
									/>
									<ColumnDirective
										field="supplierName"
										headerText="Fornecedor de Chip"
										width="200"
										textAlign="Left"
									/>
									<ColumnDirective
										headerText="Ações"
										width="140"
										textAlign="Center"
										template={(row) => (
											<div className="d-flex gap-2 justify-content-center">
												<button
													type="button"
													className="text-primary"
													onClick={() => onEditNetwork(row)}
												>
													<FaEdit size={18} />
												</button>
												<button
													type="button"
													className="text-primary"
													onClick={() => onDeleteNetwork(row)}
												>
													<FaTrash size={18} />
												</button>
											</div>
										)}
									/>
								</ColumnsDirective>
								<Inject services={[Page]} />
							</GridComponent>
						</div>
					</div>
				) : (
					""
				)}
		</>
	);
}
